import React, { PureComponent } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy } from "@fortawesome/free-solid-svg-icons";
import "assets/css/giststyles.css";

class CustomConsoleGist extends PureComponent {
  _isMounted = false;
  constructor(props) {
    super(props);
    this.gist = props.gist;
    this.file = props.file;
    this.stylesheetAdded = false;
    this.state = {
      loading: true,
      src: ""
    };
  }

  // The Gist JSON data includes a stylesheet to add to the page
  // to make it look correct. `addStylesheet` ensures we only add
  // the stylesheet one time.
  addStylesheet = href => {
    if (!this.stylesheetAdded) {
      this.stylesheetAdded = true;
      var link = document.createElement("link");
      link.type = "text/css";
      link.rel = "stylesheet";
      link.href = href;
      document.head.appendChild(link);
    }
  };

  componentDidMount() {
    this._isMounted = true;
    // Create a JSONP callback that will set our state
    // with the data that comes back from the Gist site
    var gistCallback = CustomConsoleGist.nextGistCallback();
    window[gistCallback] = function(gist) {
      if (this._isMounted) {
        this.setState({
          loading: false,
          src: gist.div
        });
      }
      this.addStylesheet(gist.stylesheet);
    }.bind(this);

    var url =
      "https://gist.github.com/" +
      this.props.gist +
      ".json?callback=" +
      gistCallback;
    if (this.props.file) {
      url += "&file=" + this.props.file;
    }

    // Add the JSONP script tag to the document.
    var script = document.createElement("script");
    script.type = "text/javascript";
    script.src = url;
    document.head.appendChild(script);
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  copyToClipboard = async (event) => {
    const button = event.currentTarget;
    const originalText = button.innerHTML;
    
    try {
      // Get the specific gist container that was clicked
      const gistContainer = button.closest('.gist-container');
      const gistFile = gistContainer.querySelector('.gist-file');
      const codeLines = gistFile.querySelectorAll('.js-file-line');
      const formattedContent = Array.from(codeLines)
        .map(line => line.textContent)
        .join('\n');
      
      await navigator.clipboard.writeText(formattedContent);
      
      // Show success state with React component instead of HTML string
      button.innerHTML = `<i class="fas fa-check"></i> Copied!`;
      button.style.backgroundColor = '#ef6c00';
      button.style.color = '#fff';
      
      // Reset after 2 seconds
      setTimeout(() => {
        button.innerHTML = originalText;
        button.style.backgroundColor = '';
        button.style.color = '';
      }, 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
      
      // Show error state with React component
      button.innerHTML = `<i class="fas fa-times"></i> Failed to copy`;
      button.style.backgroundColor = '#dc3545';
      button.style.color = '#fff';
      
      setTimeout(() => {
        button.innerHTML = originalText;
        button.style.backgroundColor = '';
        button.style.color = '';
      }, 2000);
    }
  };

  // Add custom CSS for console-like styling
  addCustomStyles() {
    if (!document.getElementById('custom-console-gist-styles')) {
      const style = document.createElement('style');
      style.id = 'custom-console-gist-styles';
      style.textContent = `
        /* Target only console gists */
        .outputBlock .gist .blob-num { 
          display: none !important; 
        }
        .outputBlock .gist .blob-code { 
          padding-left: 8px !important; 
          color: #ffffff !important;
          background-color: #000000 !important;
        }
        .outputBlock .gist .blob-wrapper { 
          border: 0 !important; 
          background-color: #000000 !important;
        }
        .outputBlock .gist .gist-data { 
          border: 0 !important; 
          border-radius: 0 !important;
          background-color: #000000 !important;
        }
        .outputBlock .gist .gist-meta {
          display: none !important;
        }
        .outputBlock .gist .highlight {
          background-color: #000000 !important;
        }
        .outputBlock .gist .pl-smi,
        .outputBlock .gist .pl-s .pl-s1,
        .outputBlock .gist .pl-s .pl-s2,
        .outputBlock .gist .pl-s .pl-c1,
        .outputBlock .gist .pl-s .pl-v,
        .outputBlock .gist .pl-s .pl-k,
        .outputBlock .gist .pl-s .pl-pds,
        .outputBlock .gist .pl-s .pl-sr,
        .outputBlock .gist .pl-s .pl-sre,
        .outputBlock .gist .pl-s .pl-sra,
        .outputBlock .gist .pl-s .pl-src,
        .outputBlock .gist .pl-s .pl-stj,
        .outputBlock .gist .pl-s .pl-vo,
        .outputBlock .gist .pl-s .pl-vpf,
        .outputBlock .gist .pl-s .pl-vpp,
        .outputBlock .gist .pl-s .pl-vpu,
        .outputBlock .gist .pl-s .pl-v {
          color: #ffffff !important;
        }
        
        /* Style the container */
        .outputBlock {
          background-color: #000000;
          padding: 0;
          margin: 0;
          border: none;
        }
        
        /* Style the copy button */
        .outputBlock .gist-copy-button {
          position: absolute;
          right: 10px;
          top: 10px;
          z-index: 1;
          background-color: #333;
          color: #fff;
          border: 1px solid #555;
          padding: 2px 8px;
          border-radius: 3px;
          cursor: pointer;
        }
        
        /* Base container styling */
        .outputBlock .gist {
          margin: 0;
          background-color: #000;
          overflow-x: auto;  /* Enable horizontal scrolling */
          padding: 0;       /* Remove all padding */
        }
        
        /* Code container */
        .outputBlock .gist .gist-data {
          padding: 0 !important;  /* Remove all padding */
          border-radius: 0 !important;
        }
        
        /* Blob wrapper */
        .outputBlock .gist .blob-wrapper {
          padding: 0 !important;  /* Remove all padding */
        }
        
        /* Code lines */
        .outputBlock .gist .blob-code {
          padding: 0 !important;  /* Remove all padding */
          line-height: 1.5 !important;
          white-space: pre !important; /* Preserve whitespace */
          font-family: monospace !important;
        }
        
        /* Ensure proper code wrapping */
        .outputBlock .gist .blob-code-inner {
          white-space: pre-wrap !important;
          word-break: break-word !important;
        }
      `;
      document.head.appendChild(style);
    }
  }

  render() {
    // Add custom styles when component mounts or updates
    if (typeof document !== 'undefined') {
      this.addCustomStyles();
    }

    if (this.state.loading) {
      return <div>loading...</div>;
    } else {
      return (
        <div className="outputBlock">
          <div className="gist-container">
            <button 
              onClick={this.copyToClipboard}
              className="gist-copy-button"
            >
              <FontAwesomeIcon icon={faCopy} /> Copy
            </button>
            <div dangerouslySetInnerHTML={{ __html: this.state.src }} />
          </div>
        </div>
      );
    }
  }
}

CustomConsoleGist.propTypes = {
  gist: PropTypes.string.isRequired,
  file: PropTypes.string
};

// Each time we request a Gist, we'll need to generate a new
// global function name to serve as the JSONP callback.
var gistCallbackId = 0;
CustomConsoleGist.nextGistCallback = () => {
  return "embed_console_gist_callback_" + gistCallbackId++;
};

export default CustomConsoleGist;
